import { useSnapshot } from "valtio";
import { Button } from "@/components/ui/button";
import {
  themeEditorStore,
  applyPreset,
  randomizeTheme,
} from "@/lib/stores/theme-editor";
import { themePresets } from "@/lib/theme/presets";
import { hslToHex } from "@/lib/theme/utils";
import { Check, Shuffle } from "lucide-react";

/**
 * Theme preset selection grid
 */
export function ThemePresets() {
  const { currentTheme, editingMode } = useSnapshot(themeEditorStore);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Choose a preset theme to start with. You can customize it further after
        selecting.
      </p>

      {/* Randomize button */}
      <Button
        variant="outline"
        className="w-full gap-2"
        onClick={randomizeTheme}
      >
        <Shuffle className="h-4 w-4" />
        Randomize Theme
      </Button>

      <div className="grid grid-cols-2 gap-3">
        {themePresets.map((preset) => {
          const isSelected = currentTheme.name === preset.name;
          const colors = editingMode === "dark" ? preset.dark : preset.light;
          const primaryHex = hslToHex(colors.primary);
          const bgHex = hslToHex(colors.background);
          const fgHex = hslToHex(colors.foreground);

          return (
            <button
              key={preset.name}
              onClick={() => applyPreset(preset)}
              className={`relative flex flex-col items-start gap-2 rounded-lg border-2 p-3 text-left transition-colors hover:bg-accent ${
                isSelected
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card"
              }`}
              style={{ borderRadius: "var(--radius)" }}
            >
              {/* Color swatches */}
              <div className="flex gap-1">
                <div
                  className="h-6 w-6 rounded-full border border-border"
                  style={{ backgroundColor: primaryHex }}
                  title="Primary"
                />
                <div
                  className="h-6 w-6 rounded-full border border-border"
                  style={{ backgroundColor: bgHex }}
                  title="Background"
                />
                <div
                  className="h-6 w-6 rounded-full border border-border"
                  style={{ backgroundColor: fgHex }}
                  title="Foreground"
                />
              </div>

              {/* Preset name */}
              <span className="text-sm font-medium">{preset.displayName}</span>

              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute right-2 top-2 rounded-full bg-primary p-0.5">
                  <Check className="h-3 w-3 text-primary-foreground" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
